import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { discoverAPI } from '../../utils/api';
import api from '../../utils/api';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, Plus, Trash2, Copy, Calendar, MapPin, Users, 
  Loader2, AlertTriangle, CheckCircle, Info, Sparkles, Crown,
  TrendingUp, ArrowRight, Zap, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const CreateEventSeries = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [subscriptionPricing, setSubscriptionPricing] = useState(null);
  
  // Mode toggle: single vs multi-event
  const [isMultiEvent, setIsMultiEvent] = useState(true);
  
  // Series info
  const [seriesName, setSeriesName] = useState('');
  const [seriesDescription, setSeriesDescription] = useState('');
  
  // Events list
  const [events, setEvents] = useState([
    {
      id: 1,
      shift_date: '',
      end_date: '',
      start_time: '09:00',
      end_time: '17:00',
      venue_name: '',
      city: '',
      state: '',
      role_id: '',
      talent_needed: 1,
      bill_rate: 25
    }
  ]);

  useEffect(() => {
    fetchRoles();
    checkSubscription();
    fetchSubscriptionPlans();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await discoverAPI.getRoles();
      setRoles(res.data || []);
    } catch (err) {
      toast.error('Failed to load roles');
    }
  };

  const checkSubscription = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/status');
      if (res.data?.has_subscription) {
        setHasSubscription(true);
        setSubscriptionTier(res.data.plan_id);
      }
    } catch (e) {
      setHasSubscription(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const res = await api.get('/monetization/client-subscriptions/plans');
      setSubscriptionPricing(res.data);
    } catch (e) {
      // Silent fail
    }
  };

  // Add a new event
  const addEvent = () => {
    const lastEvent = events[events.length - 1];
    setEvents([
      ...events,
      {
        id: Date.now(),
        shift_date: '',
        end_date: '',
        start_time: lastEvent?.start_time || '09:00',
        end_time: lastEvent?.end_time || '17:00',
        venue_name: '',
        city: lastEvent?.city || '',
        state: lastEvent?.state || '',
        role_id: lastEvent?.role_id || '',
        talent_needed: lastEvent?.talent_needed || 1,
        bill_rate: lastEvent?.bill_rate || 25
      }
    ]);
  };

  // Duplicate an event
  const duplicateEvent = (index) => {
    const eventToCopy = events[index];
    setEvents([
      ...events,
      {
        ...eventToCopy,
        id: Date.now(),
        shift_date: '', // Clear date for new entry
        end_date: ''
      }
    ]);
    toast.success(`Event ${index + 1} duplicated`);
  };

  // Remove an event
  const removeEvent = (index) => {
    if (events.length === 1) {
      toast.error('At least one event is required');
      return;
    }
    setEvents(events.filter((_, i) => i !== index));
  };

  // Update an event field
  const updateEvent = (index, field, value) => {
    const updated = [...events];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-set end_date if empty
    if (field === 'shift_date' && !updated[index].end_date) {
      updated[index].end_date = value;
    }
    
    setEvents(updated);
  };

  // Get platform fee percentage based on subscription tier
  const getPlatformFeePercent = () => {
    if (!hasSubscription) return 10;
    if (subscriptionTier === 'enterprise') return 3;
    if (subscriptionTier === 'pro') return 5;
    return 7; // basic
  };

  // Calculate pricing
  const calculatePricing = () => {
    const totalEvents = isMultiEvent ? events.length : 1;
    const eventsToUse = isMultiEvent ? events : [events[0]];
    const totalValue = eventsToUse.reduce((sum, e) => sum + (parseFloat(e.bill_rate) || 0) * (parseInt(e.talent_needed) || 1), 0);
    
    let platformFeePercent = getPlatformFeePercent();
    let perEventFee = hasSubscription ? 0 : 10.95;
    
    const eventPostingTotal = perEventFee * totalEvents;
    const platformFee = totalValue * (platformFeePercent / 100);
    
    return {
      totalEvents,
      totalValue,
      perEventFee,
      eventPostingTotal,
      platformFeePercent,
      platformFee,
      totalCost: eventPostingTotal + platformFee
    };
  };

  // Calculate savings if user upgrades to a subscription
  const calculateUpgradeSavings = () => {
    if (hasSubscription) return null;
    
    const pricing = calculatePricing();
    
    // Calculate savings for Pro tier (5% instead of 10%, no per-event fee)
    const proFee = pricing.totalValue * 0.05;
    const proSavings = pricing.totalCost - proFee;
    
    // Calculate savings for Basic tier (7% instead of 10%, no per-event fee)
    const basicFee = pricing.totalValue * 0.07;
    const basicSavings = pricing.totalCost - basicFee;
    
    return {
      basic: {
        fee: basicFee,
        savings: basicSavings,
        monthlyPrice: 99
      },
      pro: {
        fee: proFee,
        savings: proSavings,
        monthlyPrice: 199
      }
    };
  };

  const pricing = calculatePricing();
  const upgradeSavings = calculateUpgradeSavings();

  // Submit series
  const handleSubmit = async () => {
    if (!seriesName.trim() && isMultiEvent) {
      toast.error('Please enter a series/tour name');
      return;
    }
    
    const eventsToSubmit = isMultiEvent ? events : [events[0]];
    
    // Validate all events
    for (let i = 0; i < eventsToSubmit.length; i++) {
      const e = eventsToSubmit[i];
      if (!e.shift_date || !e.start_time || !e.end_time || !e.venue_name || !e.city || !e.state || !e.role_id) {
        toast.error(`Please complete all required fields for Event ${i + 1}`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const response = await api.post('/client/series', {
        name: isMultiEvent ? seriesName : `Single Event - ${eventsToSubmit[0].venue_name}`,
        description: seriesDescription,
        events: eventsToSubmit.map(e => ({
          shift_date: e.shift_date,
          end_date: e.end_date || e.shift_date,
          start_time: e.start_time,
          end_time: e.end_time,
          venue_name: e.venue_name,
          city: e.city,
          state: e.state,
          role_id: e.role_id,
          talent_needed: parseInt(e.talent_needed) || 1,
          bill_rate: parseFloat(e.bill_rate) || 25
        }))
      });
      
      const eventCount = isMultiEvent ? events.length : 1;
      toast.success(`${isMultiEvent ? `Series "${seriesName}"` : 'Event'} created with ${eventCount} event${eventCount > 1 ? 's' : ''}!`);
      navigate('/client/shifts');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create series');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to subscription page
  const handleUpgrade = () => {
    navigate('/client/settings/subscription');
  };

  const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-event-series-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/client/shifts')}
              data-testid="back-to-shifts-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Sparkles className="w-6 h-6" />
                {isMultiEvent ? 'Create Multi-Event Series' : 'Create Single Event'}
              </h1>
              <p className="text-white/80 text-sm">
                {isMultiEvent 
                  ? 'Tours, Trade Shows, Brand Activations, Multi-City Campaigns'
                  : 'Quick single event posting'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Mode Toggle */}
            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-gray-900">Event Type</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isMultiEvent 
                        ? 'Create multiple linked events under one series'
                        : 'Create a single standalone event'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${!isMultiEvent ? 'font-semibold text-purple-700' : 'text-gray-500'}`}>
                      Single
                    </span>
                    <Switch
                      checked={isMultiEvent}
                      onCheckedChange={setIsMultiEvent}
                      data-testid="multi-event-toggle"
                    />
                    <span className={`text-sm ${isMultiEvent ? 'font-semibold text-purple-700' : 'text-gray-500'}`}>
                      Multi-Event
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Series Info - Only show for multi-event */}
            {isMultiEvent && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Series / Tour Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="seriesName">Series Name *</Label>
                    <Input
                      id="seriesName"
                      placeholder="e.g., Spring Brand Tour 2026, Expo Trade Show Circuit"
                      value={seriesName}
                      onChange={(e) => setSeriesName(e.target.value)}
                      className="mt-1"
                      data-testid="series-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="seriesDescription">Description (Optional)</Label>
                    <Textarea
                      id="seriesDescription"
                      placeholder="Brief description of this series..."
                      value={seriesDescription}
                      onChange={(e) => setSeriesDescription(e.target.value)}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Events List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {isMultiEvent ? `Events (${events.length})` : 'Event Details'}
                </h2>
                {isMultiEvent && (
                  <Button 
                    onClick={addEvent} 
                    variant="outline" 
                    size="sm" 
                    data-testid="add-event-btn"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Event
                  </Button>
                )}
              </div>

              {(isMultiEvent ? events : [events[0]]).map((event, index) => (
                <Card key={event.id} className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        Event {index + 1}
                      </Badge>
                      {isMultiEvent && (
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => duplicateEvent(index)}
                            title="Duplicate this event"
                            data-testid={`duplicate-event-${index}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeEvent(index)}
                            disabled={events.length === 1}
                            data-testid={`remove-event-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Date */}
                      <div>
                        <Label className="text-xs">Start Date *</Label>
                        <Input
                          type="date"
                          value={event.shift_date}
                          onChange={(e) => updateEvent(index, 'shift_date', e.target.value)}
                          className="mt-1"
                          data-testid={`event-date-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">End Date</Label>
                        <Input
                          type="date"
                          value={event.end_date}
                          min={event.shift_date}
                          onChange={(e) => updateEvent(index, 'end_date', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Time */}
                      <div>
                        <Label className="text-xs">Start Time *</Label>
                        <Input
                          type="time"
                          value={event.start_time}
                          onChange={(e) => updateEvent(index, 'start_time', e.target.value)}
                          className="mt-1"
                          data-testid={`event-start-time-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">End Time *</Label>
                        <Input
                          type="time"
                          value={event.end_time}
                          onChange={(e) => updateEvent(index, 'end_time', e.target.value)}
                          className="mt-1"
                        />
                      </div>

                      {/* Location */}
                      <div className="col-span-2">
                        <Label className="text-xs">Venue Name * <span className="text-gray-400">(Google auto-fills address/city/state)</span></Label>
                        <AddressAutocomplete
                          value={event.venue_name}
                          onChange={(v) => updateEvent(index, 'venue_name', v)}
                          onSelect={(place) => {
                            if (!place) return;
                            updateEvent(index, 'venue_name', place.name || event.venue_name);
                            if (place.address) updateEvent(index, 'venue_address', place.address);
                            if (place.city) updateEvent(index, 'city', place.city);
                            if (place.state_code || place.state) updateEvent(index, 'state', place.state_code || place.state);
                            if (place.lat) updateEvent(index, 'venue_lat', place.lat);
                            if (place.lng) updateEvent(index, 'venue_lng', place.lng);
                          }}
                          searchType="establishment"
                          placeholder="e.g. Nashville Fairgrounds, Convention Center…"
                          country="us"
                          data-testid={`event-venue-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">City *</Label>
                        <Input
                          placeholder="e.g., Miami"
                          value={event.city}
                          onChange={(e) => updateEvent(index, 'city', e.target.value)}
                          className="mt-1"
                          data-testid={`event-city-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">State *</Label>
                        <Select value={event.state} onValueChange={(v) => updateEvent(index, 'state', v)}>
                          <SelectTrigger className="mt-1" data-testid={`event-state-${index}`}>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {US_STATES.map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Role & Staffing */}
                      <div className="col-span-2">
                        <Label className="text-xs">Role *</Label>
                        <Select value={event.role_id} onValueChange={(v) => updateEvent(index, 'role_id', v)}>
                          <SelectTrigger className="mt-1" data-testid={`event-role-${index}`}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.map(r => (
                              <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Staff Needed *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={event.talent_needed}
                          onChange={(e) => updateEvent(index, 'talent_needed', e.target.value)}
                          className="mt-1"
                          data-testid={`event-staff-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Bill Rate ($)</Label>
                        <Input
                          type="number"
                          min="15"
                          step="5"
                          value={event.bill_rate}
                          onChange={(e) => updateEvent(index, 'bill_rate', e.target.value)}
                          className="mt-1"
                          data-testid={`event-rate-${index}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Pricing Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6 bg-gradient-to-b from-slate-50 to-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-500" />
                  Pricing Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Subscription Status */}
                {hasSubscription ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="subscription-active">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium capitalize">{subscriptionTier} Plan Active</span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      No per-event fees. {pricing.platformFeePercent}% platform fee only.
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="pay-as-you-go">
                    <div className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-medium">Pay-As-You-Go</span>
                    </div>
                    <p className="text-xs text-orange-600 mt-1">
                      $10.95 per event + 10% platform fee
                    </p>
                  </div>
                )}

                {/* Pricing Breakdown */}
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Events</span>
                    <span className="font-medium" data-testid="total-events">{pricing.totalEvents}</span>
                  </div>
                  
                  {!hasSubscription && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Event Posting Fee</span>
                      <span className="font-medium" data-testid="event-posting-fee">
                        {pricing.totalEvents} x ${pricing.perEventFee.toFixed(2)} = ${pricing.eventPostingTotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Est. Booking Value</span>
                    <span className="font-medium" data-testid="booking-value">${pricing.totalValue.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Platform Fee ({pricing.platformFeePercent}%)</span>
                    <span className="font-medium" data-testid="platform-fee">${pricing.platformFee.toFixed(2)}</span>
                  </div>
                  
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-semibold">Total Platform Cost</span>
                    <span className="font-bold text-lg text-purple-600" data-testid="total-cost">
                      ${pricing.totalCost.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Upgrade CTA for Non-Subscribers */}
                {!hasSubscription && upgradeSavings && pricing.totalCost > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 space-y-3" data-testid="upgrade-cta">
                    <div className="flex items-center gap-2 text-purple-700">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-semibold text-sm">Save with a Subscription</span>
                    </div>
                    
                    {upgradeSavings.pro.savings > 5 && (
                      <div className="bg-white rounded-lg p-3 border border-purple-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">Pro Plan</p>
                            <p className="text-xs text-gray-500">$199/month</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-600 font-bold">
                              Save ${upgradeSavings.pro.savings.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">on this booking</p>
                          </div>
                        </div>
                        <p className="text-xs text-purple-600 mt-2">
                          5% platform fee, no per-event fees
                        </p>
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      onClick={handleUpgrade}
                      data-testid="upgrade-btn"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Upgrade to Save
                      <ArrowRight className="w-4 h-4 ml-auto" />
                    </Button>
                  </div>
                )}

                {/* Info Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                  <Info className="w-4 h-4 inline mr-1" />
                  Platform fees apply when bookings are confirmed. Cancel anytime before event start.
                </div>

                {/* Submit Button */}
                <Button 
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600"
                  onClick={handleSubmit}
                  disabled={loading || (isMultiEvent ? events.length === 0 : !events[0])}
                  data-testid="create-series-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      {isMultiEvent 
                        ? `Create Series (${pricing.totalEvents} Events)`
                        : 'Create Event'}
                    </>
                  )}
                </Button>

                {/* Stripe Security Note */}
                <p className="text-[10px] text-gray-400 text-center">
                  Secure payment processing by Stripe
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEventSeries;
